/**
 * Shared browser logger for `@orn/web` and `@orn/mops-web`.
 *
 * Every level except `debug` is mirrored to Sentry. `@sentry/react` is a peer
 * dependency, so the host app's `Sentry.init(...)` instance is reused — when
 * the DSN is unset (e.g. local dev) every Sentry call here becomes a no-op.
 *
 * Use the default-exported singleton:
 *
 * ```ts
 * import logger from '@orn/shared-web/lib/logger';
 *
 * logger.error('Failed to refresh balances', error, {
 *   tags: { graphql: true, operationName: 'RefreshBalances' },
 *   contexts: { graphql: { operation: 'RefreshBalances' } },
 * });
 * ```
 *
 * Do not import `@sentry/react` directly from a feature scene. Funnel through
 * the logger so we keep one path to lean on for batching / redaction / sampling.
 */
import * as Sentry from '@sentry/react';

/**
 * Sentry-shaped context accepted by `warn` / `error`.
 *
 * - `tags` — flat key/value pairs that become filterable facets in Sentry.
 *   Keep values primitive (string / number / boolean) so the UI can index them.
 * - `contexts` — structured groups of arbitrary data attached to the event
 *   (e.g. `{ graphql: { operation, query } }`). Not searchable but visible on
 *   the issue detail page.
 */
export type LogContext = {
  tags?: Record<string, string | number | boolean>;
  contexts?: Record<string, Record<string, unknown>>;
};

class Logger {
  /**
   * Console-only. Debug messages are intentionally **not** forwarded to Sentry
   * — they're for local development noise that should never reach production
   * issue trackers.
   */
  debug(message: string): void {
    // eslint-disable-next-line no-console
    console.debug(message);
    // Debug messages are not sent to Sentry
  }

  /**
   * Console + Sentry breadcrumb at `info` level. Use for noteworthy lifecycle
   * events (auth state changes, feature-flag resolution) — not for routine
   * render/render-cycle chatter.
   */
  info(message: string): void {
    // eslint-disable-next-line no-console
    console.info(message);
    Sentry.captureMessage(message, 'info');
  }

  /**
   * Console + Sentry capture at `warning` level. Pass `context` to attach
   * tags / contexts to the captured event.
   */
  warn(message: string, context?: LogContext): void {
    // eslint-disable-next-line no-console
    console.warn(message);
    Sentry.captureMessage(message, {
      level: 'warning',
      ...context,
    });
  }

  /**
   * Console + Sentry capture at `error` level.
   *
   * - When `error` is provided, calls `Sentry.captureException(error, ...)`
   *   so the stack trace is preserved and Sentry groups by error fingerprint.
   *   The human-readable `message` is attached as `extra.message`.
   * - When `error` is omitted, falls back to `Sentry.captureMessage(message, ...)`
   *   at error level — useful for "we know this failed but there's no
   *   underlying `Error` to forward."
   *
   * The `logger: true` tag is always set so events originating here can be
   * filtered apart from direct-SDK calls (e.g. `Sentry.init`,
   * `componentDidCatch`, `Sentry.setUser`).
   */
  error(message: string, error?: unknown, context?: LogContext): void {
    // eslint-disable-next-line no-console
    console.error(message, error);
    if (error !== undefined) {
      Sentry.captureException(error, {
        tags: {
          logger: true,
          ...context?.tags,
        },
        contexts: context?.contexts,
        extra: {
          message,
        },
      });
    } else {
      Sentry.captureMessage(message, {
        level: 'error',
        ...context,
        tags: {
          logger: true,
          ...context?.tags,
        },
      });
    }
  }

  /**
   * Console + Sentry capture at `info` level. Distinct from `info()` only in
   * that it routes through `console.log` rather than `console.info`, matching
   * the pre-existing `@orn/web` console-parity convention. Prefer `info` /
   * `warn` / `error` for new code; this exists to keep the migration of the
   * original `@orn/web` callers a one-line import swap.
   */
  log(message: string): void {
    // eslint-disable-next-line no-console
    console.log(message);
    Sentry.captureMessage(message, 'info');
  }
}

export default new Logger();
